import React, { useState, useRef, useEffect } from 'react';
import { Volume2, Pause } from 'lucide-react';
import { brandAssets } from '../data/mock';

const ForgedPoem = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const audioRef = useRef(null);
  const sectionRef = useRef(null);
  const animationTimeouts = useRef([]);

  // Pre-recorded ElevenLabs voiceover
  const audioUrl = "https://customer-assets.emergentagent.com/job_f0a5faf6-98e8-44c8-9627-c4f57b0dfbaf/artifacts/oqjwtcsy_BEST_PEOM_READElevenLabs_2026-04-01T23-12-38_Jon_-_Warm_Grounded_Storyteller_eleven_multilingual_v2.mp3";

  // Poem broken into words for karaoke effect
  const poemLines = [
    { words: ["In", "the", "furnace", "of", "pressure,", "the", "weak", "turn", "to", "dust,"], delay: 0 },
    { words: ["but", "the", "forged", "learn", "to", "stand,", "to", "bend", "without", "rust"], delay: 0 },
    { words: ["Every", "scar", "tells", "a", "story,", "every", "rep", "sings", "a", "creed,"], delay: 0 },
    { words: ["that", "strength", "isn't", "given", "—", "it's", "earned", "through", "the", "bleed"], delay: 0 },
    { words: ["Integrity", "carves", "the", "blade,", "perseverance", "the", "edge"], delay: 0 },
    { words: ["discipline", "sharpens", "resolve,", "no", "retreat,", "no", "hedge"], delay: 0 },
    { words: ["Resilience", "is", "armor,", "gleaming", "in", "flame,"], delay: 0 },
    { words: ["and", "purpose", "—", "the", "heartbeat", "that", "powers", "your", "name"], delay: 0 },
    { words: ["Built", "from", "pressure", "Forged", "for", "fight"], delay: 0, highlight: true },
    { words: ["A", "purpose-born", "warrior", "glowing", "in", "light"], delay: 0, highlight: true },
    { words: ["No", "shortcuts", "No", "luck", "Just", "iron", "and", "flame"], delay: 0 },
    { words: ["EFAM", "—", "THE", "SYMBOL,", "THE", "SPIRIT,", "THE", "NAME"], delay: 0, final: true },
  ];

  // Calculate total word count for indexing
  const allWords = poemLines.flatMap(line => line.words);
  const totalWords = allWords.length;

  // Clear all animation timeouts
  const clearAnimations = () => {
    animationTimeouts.current.forEach(timeout => clearTimeout(timeout));
    animationTimeouts.current = [];
  };

  // Start karaoke animation
  const startKaraokeAnimation = () => {
    clearAnimations();
    setActiveWordIndex(-1);
    
    // Timing per word (adjust to match your audio speed)
    const msPerWord = 380; // Milliseconds per word
    const initialDelay = 1000; // Delay before first word
    
    allWords.forEach((_, index) => {
      const timeout = setTimeout(() => {
        setActiveWordIndex(index);
      }, initialDelay + (index * msPerWord));
      animationTimeouts.current.push(timeout);
    });
  };

  // Autoplay when section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAutoPlayed && audioRef.current) {
            setTimeout(() => {
              audioRef.current.play()
                .then(() => {
                  setIsPlaying(true);
                  setHasAutoPlayed(true);
                  startKaraokeAnimation();
                })
                .catch((error) => {
                  console.log('Autoplay blocked, user interaction required');
                });
            }, 500);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
      clearAnimations();
    };
  }, [hasAutoPlayed]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      clearAnimations();
    } else {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
      startKaraokeAnimation();
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
    // Keep all words lit at the end
    setActiveWordIndex(totalWords);
  };

  // Calculate the word index offset for each line
  let wordOffset = 0;

  return (
    <section 
      id="forged-poem" 
      ref={sectionRef}
      className="py-24 bg-black relative overflow-hidden"
    >
      {/* Hidden Audio Element */}
      <audio 
        ref={audioRef} 
        src={audioUrl} 
        onEnded={handleAudioEnd}
        preload="auto"
      />

      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/4 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-cyan-400 neon-text mb-2" data-testid="poem-headline">
              FORGED FOR
            </h2>
            <h2 className="text-5xl md:text-6xl font-black text-cyan-400 neon-text">
              PURPOSE
            </h2>
          </div>

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left - EFAM Logo */}
            <div className="flex flex-col items-center justify-start">
              <div className="relative w-full max-w-md mx-auto">
                <img
                  src={brandAssets.hero}
                  alt="EFAM"
                  className="w-full h-auto object-contain hero-image-blend"
                  data-testid="poem-figure"
                />
              </div>
              
              {/* Audio Button */}
              <button
                onClick={handlePlayPause}
                className={`mt-8 flex items-center space-x-3 transition-colors group px-6 py-3 rounded-full border ${
                  isPlaying 
                    ? 'text-cyan-400 border-cyan-400 bg-cyan-400/10' 
                    : 'text-gray-400 border-gray-600 hover:text-cyan-400 hover:border-cyan-400'
                }`}
                data-testid="poem-audio-btn"
              >
                {isPlaying ? (
                  <>
                    <Pause className="text-cyan-400 animate-pulse" size={24} />
                    <span className="text-sm uppercase tracking-wider">Playing</span>
                  </>
                ) : (
                  <>
                    <Volume2 className="group-hover:text-cyan-400" size={24} />
                    <span className="text-sm uppercase tracking-wider">Listen</span>
                  </>
                )}
              </button>
            </div>

            {/* Right - Poem with Karaoke Effect */}
            <div className="space-y-4 leading-relaxed">
              {poemLines.map((line, lineIndex) => {
                const lineStartIndex = wordOffset;
                wordOffset += line.words.length;
                
                return (
                  <p 
                    key={lineIndex} 
                    className={`text-lg ${line.final ? 'text-xl mt-8 font-bold' : ''}`}
                  >
                    {line.words.map((word, wordIndex) => {
                      const globalIndex = lineStartIndex + wordIndex;
                      const isActive = globalIndex <= activeWordIndex;
                      const isHighlightLine = line.highlight;
                      const isFinalLine = line.final;
                      
                      return (
                        <span
                          key={wordIndex}
                          className={`
                            inline-block mr-2 transition-all duration-300
                            ${isActive 
                              ? isFinalLine
                                ? 'text-cyan-400 karaoke-glow-strong scale-105'
                                : isHighlightLine 
                                  ? 'text-cyan-400 karaoke-glow font-semibold' 
                                  : 'text-white karaoke-glow'
                              : 'text-white opacity-40'
                            }
                          `}
                          style={{
                            textShadow: isActive 
                              ? isFinalLine 
                                ? '0 0 10px #22d3ee, 0 0 20px #22d3ee, 0 0 40px #22d3ee, 0 0 60px #22d3ee'
                                : '0 0 8px #22d3ee, 0 0 16px #22d3ee, 0 0 24px #22d3ee'
                              : 'none',
                            transform: isActive ? 'scale(1.02)' : 'scale(1)'
                          }}
                        >
                          {word}
                        </span>
                      );
                    })}
                  </p>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Dimming overlay when audio plays */}
      {isPlaying && (
        <div className="fixed inset-0 bg-black/30 z-[-1] transition-opacity duration-500 pointer-events-none"></div>
      )}

      {/* Inline styles for karaoke glow */}
      <style>{`
        .karaoke-glow {
          text-shadow: 0 0 8px #22d3ee, 0 0 16px #22d3ee, 0 0 24px #22d3ee;
        }
        .karaoke-glow-strong {
          text-shadow: 0 0 10px #22d3ee, 0 0 20px #22d3ee, 0 0 40px #22d3ee, 0 0 60px #22d3ee;
        }
      `}</style>
    </section>
  );
};

export default ForgedPoem;
